import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { getLocale } from 'umi';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import MetricsCardWrap from '@/pages/dashboard/components/MetricsCardWrap';
import MetricsCardValue from '@/pages/dashboard/components/MetricsCardValue';
import CircularProgress from '@/pages/dashboard/components/CircularProgress';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';
import {
  getMetricValue,
  calcPercent,
  getDateRangeText,
  getMonthTargetLabel,
  getMonthFirstDate,
  getEndDate,
  resolveDataKey,
  isFilterOverridesMatched,
} from '@/pages/dashboard/utils/kpiCardUtils';
import type { IKpiSummaryCardProps, IKpiExtraMetric, TKpiSummaryTargetModule } from './types';
import './index.scss';

const DEFAULT_LOADING_HEIGHT = 186;

const KpiSummaryCard: React.FC<IKpiSummaryCardProps> = (props) => {
  const mergedProps = { ...props, ...(props.config?.props || {}) };
  const {
    data,
    className,
    style,
    titleCn,
    titleEn,
    icon,
    valueMetric,
    targetMetric,
    formatRule = 'thousandsInit',
    preUnit,
    targetFormatRule,
    targetPreUnit,
    clickable,
    jumpUrl,
    extraMetrics,
    targetLabelCn,
    targetLabelEn,
    subtitleCn,
    subtitleEn,
    dataKey: explicitDataKey,
    valueDataKey: explicitValueDataKey,
    targetDataKey: explicitTargetDataKey,
    extraMetricsDataKey: explicitExtraMetricsDataKey,
    loadingHeight = DEFAULT_LOADING_HEIGHT,
    targetProgressFilterOverrides = [],
    hiddenTargetModules = [],
    hiddenTargetModulesWhenFilterMismatch,
  } = mergedProps;

  const isEN = getLocale() === LANGUAGE_EN;
  const { filterVal } = useContext(FilterContext);

  const dataKey = useMemo(() => resolveDataKey(data, explicitDataKey), [data, explicitDataKey]);
  const valueDataKey = useMemo(
    () => resolveDataKey(data, explicitValueDataKey || explicitDataKey),
    [data, explicitDataKey, explicitValueDataKey],
  );
  const targetDataKey = useMemo(
    () => resolveDataKey(data, explicitTargetDataKey || explicitDataKey),
    [data, explicitDataKey, explicitTargetDataKey],
  );
  const extraMetricsDataKey = useMemo(
    () => resolveDataKey(data, explicitExtraMetricsDataKey || explicitTargetDataKey || explicitDataKey),
    [data, explicitDataKey, explicitExtraMetricsDataKey, explicitTargetDataKey],
  );

  const metricsMap = data?.[dataKey]?.data;
  const valueMetricsMap = data?.[valueDataKey]?.data;
  const targetMetricsMap = data?.[targetDataKey]?.data;
  const extraMetricsMap = data?.[extraMetricsDataKey]?.data;
  const loading = Boolean(
    data?.[dataKey]?.loading
    || data?.[valueDataKey]?.loading
    || data?.[targetDataKey]?.loading
    || data?.[extraMetricsDataKey]?.loading,
  );
  const errorMsg = data?.[dataKey]?.errorMsg
    || data?.[valueDataKey]?.errorMsg
    || data?.[targetDataKey]?.errorMsg
    || data?.[extraMetricsDataKey]?.errorMsg;

  const actualValue = getMetricValue(valueMetricsMap || metricsMap, valueMetric);
  const targetValue = getMetricValue(targetMetricsMap || metricsMap, targetMetric);
  const percent = calcPercent(actualValue, targetValue);

  const subtitleText = useMemo(() => {
    const staticText = getName(isEN, subtitleEn, subtitleCn);
    return staticText || getDateRangeText(filterVal);
  }, [filterVal, isEN, subtitleCn, subtitleEn]);

  const targetLabel = useMemo(
    () => getMonthTargetLabel(filterVal, isEN, targetLabelCn, targetLabelEn),
    [filterVal, isEN, targetLabelCn, targetLabelEn],
  );
  const filterOverridesMatched = useMemo(() => {
    if (targetProgressFilterOverrides.length === 0) return true;
    return isFilterOverridesMatched(filterVal, targetProgressFilterOverrides);
  }, [filterVal, targetProgressFilterOverrides]);

  const mismatchHiddenModules = useMemo(
    () => hiddenTargetModulesWhenFilterMismatch ?? [],
    [hiddenTargetModulesWhenFilterMismatch],
  );

  const isTargetModuleHidden = useCallback(
    (key: TKpiSummaryTargetModule): boolean => {
      if (hiddenTargetModules.includes(key)) return true;
      if (!filterOverridesMatched && mismatchHiddenModules.includes(key)) {
        return true;
      }
      return false;
    },
    [
      hiddenTargetModules,
      filterOverridesMatched,
      mismatchHiddenModules,
    ],
  );

  const link = useMemo(() => {
    if (!clickable || !jumpUrl || jumpUrl === 'none') return undefined;
    return jumpUrl
      .replace(/\{\{monthFirst\}\}/g, getMonthFirstDate(filterVal))
      .replace(/\{\{endDate\}\}/g, getEndDate(filterVal));
  }, [clickable, jumpUrl, filterVal]);

  const bodyContent = (
    <div className="dbd-kpi-summary-card__body">
      <div className="dbd-kpi-summary-card__left">
        <MetricsCardValue
          value={actualValue}
          formatRule={formatRule}
          preUnit={preUnit}
          emptyValue="-"
        />
        {(!isTargetModuleHidden('target')
          || !isTargetModuleHidden('extraMetrics')) && (
          <div className="dbd-kpi-summary-card__grid">
            {!isTargetModuleHidden('target') && (
              <>
                <span className="dbd-kpi-summary-card__label">{targetLabel}</span>
                <span className="dbd-kpi-summary-card__val-col dbd-kpi-summary-card__val-col--wide">
                  <MetricsCardValue
                    className="dbd-kpi-summary-card__target-val"
                    value={targetValue}
                    formatRule={targetFormatRule || formatRule}
                    preUnit={targetPreUnit ?? preUnit}
                    emptyValue="-"
                  />
                </span>
              </>
            )}
            {!isTargetModuleHidden('extraMetrics')
              && extraMetrics
              && extraMetrics.length > 0
              && extraMetrics.map((m: IKpiExtraMetric) => {
                const val = getMetricValue(extraMetricsMap || metricsMap, m.valueMetric);
                const targetVal = m.targetMetric ? getMetricValue(extraMetricsMap || metricsMap, m.targetMetric) : '';
                const numVal = parseFloat(val);
                const numTarget = parseFloat(targetVal);
                const canCalcRatio = m.targetMetric && !isNaN(numVal) && !isNaN(numTarget) && numTarget !== 0;
                const ratioText = canCalcRatio
                  ? `(${Math.round((numVal / numTarget) * 100)}%)`
                  : '(-)';
                return (
                  <React.Fragment key={m.valueMetric}>
                    <span className="dbd-kpi-summary-card__label">
                      {getName(isEN, m.labelEn, m.labelCn)}
                    </span>
                    <span className="dbd-kpi-summary-card__val-col">
                      <MetricsCardValue
                        className="dbd-kpi-summary-card__target-val"
                        value={val}
                        formatRule={m.formatRule || formatRule}
                        preUnit={m.preUnit || preUnit}
                        emptyValue="-"
                      />
                      {m.targetMetric && (
                        <span className="dbd-kpi-summary-card__ratio">
                          {ratioText}
                        </span>
                      )}
                    </span>
                  </React.Fragment>
                );
              })}
          </div>
        )}
      </div>
      {!isTargetModuleHidden('circleChart') && (
        <div className="dbd-kpi-summary-card__circle">
          <CircularProgress percent={percent} />
        </div>
      )}
    </div>
  );

  const card = (
    <MetricsCardWrap
      titleCn={titleCn}
      titleEn={titleEn}
      icon={icon}
      link={link}
      className={classNames('dbd-kpi-card', 'dbd-kpi-card--summary', className)}
      style={style}
      contentHeader={subtitleText ? <div className="dbd-kpi-card__date">{subtitleText}</div> : undefined}
    >
      {bodyContent}
    </MetricsCardWrap>
  );

  return (
    <Abnormal
      isLoading={loading}
      isError={!!errorMsg}
      className={classNames('dbd-kpi-card-loading', 'dbd-kpi-card-loading--summary', className)}
      style={{ height: loadingHeight }}
      component={card}
    />
  );
};

export default KpiSummaryCard;
